import React, { useEffect, useRef, useState } from 'react'
import { AiOutlineArrowLeft, AiOutlineFullscreen, AiOutlineFullscreenExit } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'

const InterferenceDiffraction = () => {
  const navigate = useNavigate()
  const canvasRef = useRef(null)
  const dividerRef = useRef(null)
  const containerRef = useRef(null)
  const [slitWidth, setSlitWidth] = useState(12.0)
  const [slitSeparation, setSlitSeparation] = useState(36.0)
  const [screenDistance, setScreenDistance] = useState(1.0)
  const [wavelength, setWavelength] = useState(0.65)
  const [openingType, setOpeningType] = useState(3)
  const [controlsWidth, setControlsWidth] = useState(30)
  const [isFullScreen, setIsFullScreen] = useState(false)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    let animationFrameId

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth
      canvas.height = canvas.offsetHeight
      drawMotion()
    }

    const drawMotion = () => {
      // Clear the canvas
      context.clearRect(0, 0, canvas.width, canvas.height)

      // Set background colors
      context.fillStyle = '#e0e0e0'
      context.fillRect(0, 0, canvas.width, canvas.height)

      // Calculate new dimensions based on screen size
      const graphWidth = canvas.width - 100
      const graphHeight = Math.min(canvas.height - 230, graphWidth * 0.4)
      const xBase = 50
      const yBase = (canvas.height - graphHeight) / 2 - 40

      context.fillStyle = '#eff'
      context.fillRect(xBase, yBase, graphWidth, graphHeight)
      context.fillStyle = 'black'
      context.fillRect(xBase, yBase + graphHeight + 70, graphWidth, 50)
      context.fillRect(xBase, yBase - 80, graphWidth, 50)

      // Draw grid lines and axes
      drawGridAndAxes(context, xBase, yBase, graphWidth, graphHeight)

      // Draw the interference pattern
      drawInterferencePattern(context, xBase, yBase, graphWidth, graphHeight)

      // Request next animation frame
      animationFrameId = requestAnimationFrame(drawMotion)
    }

    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Add event listeners for resizing
    const divider = dividerRef.current
    let isDragging = false

    const startDragging = e => {
      isDragging = true
    }

    const stopDragging = e => {
      isDragging = false
    }

    const drag = e => {
      if (!isDragging) return
      const containerWidth = divider.parentElement.clientWidth
      const newWidth = ((containerWidth - e.clientX) / containerWidth) * 100
      setControlsWidth(Math.min(Math.max(newWidth, 20), 80)) // Limit between 20% and 80%
    }

    divider.addEventListener('mousedown', startDragging)
    document.addEventListener('mousemove', drag)
    document.addEventListener('mouseup', stopDragging)

    return () => {
      cancelAnimationFrame(animationFrameId)
      window.removeEventListener('resize', resizeCanvas)
      divider.removeEventListener('mousedown', startDragging)
      document.removeEventListener('mousemove', drag)
      document.removeEventListener('mouseup', stopDragging)
    }
  }, [slitWidth, slitSeparation, screenDistance, wavelength, openingType])

  const drawGridAndAxes = (context, xBase, yBase, graphWidth, graphHeight) => {
    // Draw grid lines
    context.strokeStyle = '#000'
    context.lineWidth = 1
    for (let i = 0; i <= 10; i++) {
      context.beginPath()
      context.moveTo(xBase + (graphWidth / 10) * i, yBase)
      context.lineTo(xBase + (graphWidth / 10) * i, yBase + graphHeight)
      context.stroke()

      // Add x-axis values
      context.font = '14pt Calibri'
      context.fillStyle = 'black'
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      const axisValue = -10 + 2 * i
      const axisLabel = axisValue.toFixed(0)
      context.fillText(axisLabel, xBase + (graphWidth / 10) * i, yBase + graphHeight + 25)
    }

    // Draw horizontal grid lines
    for (let i = 0; i <= 4; i++) {
      context.beginPath()
      context.moveTo(xBase - 10, yBase + (graphHeight / 4) * i)
      context.lineTo(xBase + graphWidth, yBase + (graphHeight / 4) * i)
      context.stroke()
    }

    // Draw x-axis
    context.strokeStyle = '#000'
    context.lineWidth = 4
    context.beginPath()
    context.moveTo(xBase - 1, yBase + graphHeight)
    context.lineTo(xBase + graphWidth + 20, yBase + graphHeight)
    context.stroke()

    // Draw x-axis arrow
    context.moveTo(xBase + graphWidth + 10, yBase + graphHeight - 6)
    context.lineTo(xBase + graphWidth + 20, yBase + graphHeight)
    context.lineTo(xBase + graphWidth + 10, yBase + graphHeight + 6)
    context.lineJoin = 'miter'
    context.stroke()

    // Update x-axis label position
    context.font = '16pt Calibri'
    context.fillStyle = 'black'
    context.textAlign = 'center'
    context.textBaseline = 'top'
    context.fillText('x (cm)', xBase + graphWidth / 2, yBase + graphHeight + 45)

    // Draw y-axis
    context.beginPath()
    context.moveTo(xBase, yBase - 20)
    context.lineTo(xBase, yBase + graphHeight)
    context.stroke()

    // Draw y-axis arrow
    context.moveTo(xBase - 6, yBase - 10)
    context.lineTo(xBase, yBase - 20)
    context.lineTo(xBase + 6, yBase - 10)
    context.lineJoin = 'miter'
    context.stroke()

    // Update y-axis label position and rotation
    context.save()
    context.translate(xBase - 30, yBase + graphHeight / 2)
    context.rotate(-Math.PI / 2)
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.fillText('Intensity', 0, 0)
    context.restore()
  }

  const drawInterferencePattern = (context, xBase, yBase, graphWidth, graphHeight) => {
    context.lineWidth = 2
    const originalColor = getWavelengthColor()
    context.strokeStyle = originalColor
    context.fillStyle = originalColor

    const drawPattern = (formula, color = context.strokeStyle) => {
      context.beginPath()
      context.strokeStyle = color
      for (let x = 0; x <= graphWidth; x++) {
        const ival = (x / graphWidth) * 400 - 200 // Scale x to range from -200 to 200
        const y = formula(ival)
        context.lineTo(xBase + x, y)
      }
      context.stroke()
    }

    if (openingType === 1) {
      // Single slit
      drawPattern(ival => {
        const angle = (0.00154 * ival * slitWidth) / (wavelength * screenDistance)
        return yBase + graphHeight - graphHeight * (Math.sin(angle) / angle) ** 2
      })

      // Draw slit
      context.fillRect(xBase + graphWidth / 2 - slitWidth / 2, yBase + graphHeight + 75, slitWidth, 40)
    } else if (openingType === 2) {
      // Double source
      drawPattern(ival => {
        const angle = (0.00308 * ival * slitSeparation) / (wavelength * screenDistance)
        return yBase + graphHeight - (graphHeight * (1 + Math.cos(angle))) / 2
      })

      // Draw sources
      context.beginPath()
      context.arc(xBase + graphWidth / 2 - slitSeparation / 2, yBase + graphHeight + 95, 3, 0, 2 * Math.PI)
      context.fill()
      context.beginPath()
      context.arc(xBase + graphWidth / 2 + slitSeparation / 2, yBase + graphHeight + 95, 3, 0, 2 * Math.PI)
      context.fill()
    } else if (openingType === 3) {
      // Double slit
      // Draw diffraction envelope in light grey
      drawPattern(
        ival => {
          const angle = (0.00154 * ival * slitWidth) / (wavelength * screenDistance)
          return yBase + graphHeight - graphHeight * (Math.sin(angle) / angle) ** 2
        },
        'rgba(128, 128, 128, 0.5)' // Light grey color
      )

      // Draw interference pattern in original color
      drawPattern(ival => {
        const singleSlitAngle = (0.00154 * ival * slitWidth) / (wavelength * screenDistance)
        const doubleSlitAngle = (0.00308 * ival * slitSeparation) / (wavelength * screenDistance)
        return (
          yBase +
          graphHeight -
          (graphHeight * (1 + Math.cos(doubleSlitAngle)) * (Math.sin(singleSlitAngle) / singleSlitAngle) ** 2) / 2
        )
      }, originalColor)

      // Draw slits
      context.fillRect(
        xBase + graphWidth / 2 - slitSeparation / 2 - slitWidth / 2,
        yBase + graphHeight + 75,
        slitWidth,
        40
      )
      context.fillRect(
        xBase + graphWidth / 2 + slitSeparation / 2 - slitWidth / 2,
        yBase + graphHeight + 75,
        slitWidth,
        40
      )
    }

    // Draw intensity on screen
    for (let x = 0; x < graphWidth; x++) {
      const ival = (x / graphWidth) * 400 - 200
      let intensity
      if (openingType === 1) {
        const angle = (0.00154 * ival * slitWidth) / (wavelength * screenDistance)
        intensity = (Math.sin(angle) / angle) ** 2
      } else if (openingType === 2) {
        const angle = (0.00308 * ival * slitSeparation) / (wavelength * screenDistance)
        intensity = (1 + Math.cos(angle)) / 2
      } else {
        const singleSlitAngle = (0.00154 * ival * slitWidth) / (wavelength * screenDistance)
        const doubleSlitAngle = (0.00308 * ival * slitSeparation) / (wavelength * screenDistance)
        intensity = ((1 + Math.cos(doubleSlitAngle)) * (Math.sin(singleSlitAngle) / singleSlitAngle) ** 2) / 2
      }
      const color = getIntensityColor(Math.floor(intensity * 255))
      context.strokeStyle = color
      context.beginPath()
      context.moveTo(xBase + x, yBase - 75)
      context.lineTo(xBase + x, yBase - 35)
      context.stroke()
    }
  }

  const getWavelengthColor = () => {
    if (wavelength > 0.64) return 'red'
    if (wavelength < 0.41) return '#cc00ff'
    return 'green'
  }

  const getIntensityColor = intensity => {
    let r = intensity,
      g = intensity,
      b = intensity
    if (wavelength > 0.64) {
      g = b = 1
    } else if (wavelength < 0.41) {
      g = 1
    } else {
      r = b = 1
    }
    return `rgb(${r}, ${g}, ${b})`
  }

  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch(err => {
        console.error(`Error attempting to enable full-screen mode: ${err.message}`)
      })
    } else {
      document.exitFullscreen()
    }
  }

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      style={{
        display: 'flex',
        width: '100%',
        height: '100vh',
        backgroundColor: '#e0e0e0',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', width: `${100 - controlsWidth}%`, height: '100%' }}>
        <h1
          style={{
            textAlign: 'center',
            color: '#333',
            fontSize: '1.5em',
            fontWeight: 'bold',
            padding: '10px 0',
            margin: '0',
          }}
        >
          Intensity as a function of position on screen
        </h1>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }}></canvas>
        </div>
      </div>
      <div
        ref={dividerRef}
        style={{
          width: '5px',
          backgroundColor: '#e0e0e0',
          cursor: 'col-resize',
          transition: 'background-color 0.3s',
        }}
        onMouseEnter={e => (e.target.style.backgroundColor = '#e0e0e0')}
        onMouseLeave={e => (e.target.style.backgroundColor = '#e0e0e0')}
      ></div>
      <div
        style={{
          width: `${controlsWidth}%`,
          height: '100%',
          padding: '20px',
          backgroundColor: '#e0e0e0',
          boxShadow: '-4px 0 6px rgba(0, 0, 0, 0.1)',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <div style={{ position: 'absolute', top: '20px', left: '20px', cursor: 'pointer' }}>
          <AiOutlineArrowLeft className="text-gray-600 text-3xl hover:text-gray-800" onClick={() => navigate('/')} />
        </div>
        <div style={{ position: 'absolute', top: '20px', right: '20px', cursor: 'pointer' }}>
          {isFullScreen ? (
            <AiOutlineFullscreenExit
              className="text-gray-600 text-3xl hover:text-gray-800"
              onClick={toggleFullScreen}
            />
          ) : (
            <AiOutlineFullscreen className="text-gray-600 text-3xl hover:text-gray-800" onClick={toggleFullScreen} />
          )}
        </div>
        <div style={{ marginTop: '50px' }}>
          <div>
            <label>Slit width (micrometers): {slitWidth}</label>
            <input
              type="range"
              min="8"
              max="16"
              step="0.5"
              value={slitWidth}
              onChange={e => setSlitWidth(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <label>Distance between slits (micrometers): {slitSeparation}</label>
            <input
              type="range"
              min="20"
              max="72"
              step="1.0"
              value={slitSeparation}
              onChange={e => setSlitSeparation(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <label>Distance to the screen (meters): {screenDistance}</label>
            <input
              type="range"
              min="1"
              max="2"
              step="0.01"
              value={screenDistance}
              onChange={e => setScreenDistance(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Color of light:</label>
            <div>
              <button
                onClick={() => setWavelength(0.65)}
                style={{
                  backgroundColor: wavelength === 0.65 ? '#f44336' : '#eee',
                  color: wavelength === 0.65 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Red
              </button>
              <button
                onClick={() => setWavelength(0.52)}
                style={{
                  backgroundColor: wavelength === 0.52 ? '#4caf50' : '#eee',
                  color: wavelength === 0.52 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Green
              </button>
              <button
                onClick={() => setWavelength(0.4)}
                style={{
                  backgroundColor: wavelength === 0.4 ? '#9c27b0' : '#eee',
                  color: wavelength === 0.4 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Violet
              </button>
            </div>
          </div>
          <div style={{ marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Type of opening:</label>
            <div>
              <button
                onClick={() => setOpeningType(1)}
                style={{
                  backgroundColor: openingType === 1 ? '#2196f3' : '#eee',
                  color: openingType === 1 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Single slit
              </button>
              <button
                onClick={() => setOpeningType(2)}
                style={{
                  backgroundColor: openingType === 2 ? '#2196f3' : '#eee',
                  color: openingType === 2 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Double source
              </button>
              <button
                onClick={() => setOpeningType(3)}
                style={{
                  backgroundColor: openingType === 3 ? '#2196f3' : '#eee',
                  color: openingType === 3 ? 'white' : 'black',
                  border: 'none',
                  padding: '8px 16px',
                  margin: '0 5px 5px 0',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.9em',
                }}
              >
                Double slit
              </button>
            </div>
          </div>
          <p style={{ marginTop: '20px', fontSize: '0.8em', color: '#666' }}>
            Adapted from:{' '}
            <a
              rel="license"
              href="https://physics.bu.edu/~duffy/classroom.html"
              style={{ color: '#2196f3', textDecoration: 'none' }}
            >
              https://physics.bu.edu/~duffy/classroom.html
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}

export default InterferenceDiffraction
